'use client';

import React, { createContext, useContext, useState } from 'react';
import ErrorCodeHelper from '@dealroadshow/error-codes';
import { RpcError } from '@dealroadshow/json-rpc-dispatcher';
import PhysicalDeliveryRepository from '../infrastructure/PhysicalDeliveryRepository';
import DataroomErrorHandler from '@dataroom/error-handler';
import { AlertManager } from '@dealroadshow/uikit-alert';
import getMessage from '@framework/message/getMessage';
import { messageCodes } from '@framework/message/messages';
import CountryRepository from '@dm-portal/country/CountryRepository';
import CountryRepositoryI from '../infrastructure/CountryRepositoryI';
import { ICountry } from '@dataroom/entity/Country';
import { getContainer } from '@framework/di/getContainer';

const usePhysicalDelivery = () => {
  const container = getContainer();
  const [isRequestCopyFetching, setIsRequestCopyFetching] = useState(false);
  const [isCountriesFetching, setIsCountriesFetching] = useState(false);
  const [isDataroomDisplayNameFetching, setIsDataroomDisplayNameFetching] = useState(false);
  const [isTokenValidationFetching, setIsTokenValidationFetching] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [countriesCollection, setCountriesCollection] = useState<ICountry[]>([]);
  const [dataroomDisplayName, setDataroomDisplayName] = useState('');
  const [docState, setDocState] = useState('');
  const [canRequestPhysicalCopy, setCanRequestPhysicalCopy] = useState(false);
  const [isTokenNotValid, setIsNotTokenValid] = useState(false);

  const sendRequestCopy = async (
    formData: any,
    name: string,
    token: string,
    isShowSuccessMessage?: boolean,
  ) => {
    setIsRequestCopyFetching(true);

    const payload = {
      ...formData,
      dataroomName: name,
      phoneNumber: formData.phoneNumber.value,
      token,
    };

    try {
      const physicalDeliveryRepository = container.get<PhysicalDeliveryRepository>(PhysicalDeliveryRepository);
      await physicalDeliveryRepository.requestCopy(payload);
      if (isShowSuccessMessage) {
        AlertManager.success(getMessage(messageCodes.DATAROOM_REQUEST_PHYSICAL_COPY_SUCCESS));
      }
    } catch (error) {
      container.get(DataroomErrorHandler).handleError(error);
    } finally {
      setIsRequestCopyFetching(false);
    }
  };

  const checkHasAccess = async (dataroomName: string, token: string) => {
    setIsRequestCopyFetching(true);

    try {
      const physicalDeliveryRepository = container.get<PhysicalDeliveryRepository>(PhysicalDeliveryRepository);
      const response = await physicalDeliveryRepository.hasAccess({ dataroomName, token });
      setHasAccess(response.hasAccess);
    } catch (error) {
      container.get(DataroomErrorHandler).handleError(error);
    } finally {
      setIsRequestCopyFetching(false);
    }
  };

  const getCountriesList = async (token: string) => {
    setIsCountriesFetching(true);

    const payload = { token };

    try {
      const countryRepository: CountryRepositoryI = token
        ? container.get(PhysicalDeliveryRepository)
        : container.get(CountryRepository) as CountryRepositoryI;
      const response = await countryRepository.getCountries(payload);
      setCountriesCollection(response);
    } catch (error) {
      container.get(DataroomErrorHandler).handleError(error);
    } finally {
      setIsCountriesFetching(false);
    }
  };

  const getDataroomDisplayName = async (token: string) => {
    setIsDataroomDisplayNameFetching(true);

    try {
      const physicalDeliveryRepository = container.get<PhysicalDeliveryRepository>(PhysicalDeliveryRepository);
      const response = await physicalDeliveryRepository.getDataroomDisplayName({ token });
      setDataroomDisplayName(response.displayName);
    } catch (error) {
      container.get(DataroomErrorHandler).handleError(error);
    } finally {
      setIsDataroomDisplayNameFetching(false);
    }
  };

  const checkValidTokenAndDataroomSettings = async (token: string) => {
    setIsTokenValidationFetching(true);

    try {
      const physicalDeliveryRepository = container.get<PhysicalDeliveryRepository>(PhysicalDeliveryRepository);
      const response = await physicalDeliveryRepository.getDataroomSettings({ token });
      setDocState(response.docState);
      setCanRequestPhysicalCopy(response.requestPhysicalCopy);
    } catch (error) {
      if (error instanceof RpcError && error.getCode() === ErrorCodeHelper.getCodeByName('USER_AUTH_ERROR')) {
        setIsNotTokenValid(true);
      } else {
        container.get(DataroomErrorHandler).handleError(error);
      }
    } finally {
      setIsTokenValidationFetching(false);
    }
  };

  return {
    isRequestCopyFetching,
    isCountriesFetching,
    isDataroomDisplayNameFetching,
    isTokenValidationFetching,
    hasAccess,
    isTokenNotValid,
    countriesCollection,
    dataroomDisplayName,
    docState,
    canRequestPhysicalCopy,
    sendRequestCopy,
    checkHasAccess,
    getCountriesList,
    getDataroomDisplayName,
    checkValidTokenAndDataroomSettings,
  };
};

type PhysicalDeliveryType = ReturnType<typeof usePhysicalDelivery>;

export const PhysicalDeliveryContext = createContext<PhysicalDeliveryType | null>(null);

export function usePhysicalDeliveryContext() {
  const context = useContext(PhysicalDeliveryContext);
  if (!context) {
    throw new Error('usePhysicalDeliveryContext must be used within a PhysicalDeliveryContext');
  }
  return context;
}

function PhysicalDeliveryProvider({ children }: { children?: React.ReactNode }) {
  return (
    <PhysicalDeliveryContext.Provider value={ usePhysicalDelivery() }>
      { children }
    </PhysicalDeliveryContext.Provider>
  );
}

export default PhysicalDeliveryProvider;
